/**
 * VisibilitySelector — shared component for selecting board/library visibility mode.
 * Uses Radix UI Select with three options: Private, Public (view only), Public (can edit).
 */

import React, { useState } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type VisibilityMode = 'private' | 'public-read' | 'public-edit';

export interface VisibilitySelectorProps {
  readonly currentVisibility: VisibilityMode;
  readonly onVisibilityChange: (newVisibility: VisibilityMode) => Promise<void>;
  readonly disabled?: boolean;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const VISIBILITY_OPTIONS: { value: VisibilityMode; label: string; description: string }[] = [
  { value: 'private', label: 'Private', description: 'Only members can access' },
  { value: 'public-read', label: 'Public (view only)', description: 'Anyone can view, only members can edit' },
  { value: 'public-edit', label: 'Public (can edit)', description: 'Anyone can view and edit' },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function VisibilitySelector({
  currentVisibility,
  onVisibilityChange,
  disabled = false,
}: VisibilitySelectorProps): React.ReactElement {
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleValueChange = async (value: string): Promise<void> => {
    const newVisibility = value as VisibilityMode;
    if (newVisibility === currentVisibility) return;

    setIsUpdating(true);
    setError(null);
    try {
      await onVisibilityChange(newVisibility);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to update visibility';
      setError(message);
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className="space-y-2">
      <label className="text-sm font-medium text-foreground">Visibility</label>
      <Select
        value={currentVisibility}
        onValueChange={handleValueChange}
        disabled={disabled || isUpdating}
      >
        <SelectTrigger className="w-full" aria-label="Board visibility">
          <SelectValue placeholder="Select visibility…" />
        </SelectTrigger>
        <SelectContent>
          {VISIBILITY_OPTIONS.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              <div className="flex flex-col">
                <span className="font-medium">{option.label}</span>
                <span className="text-xs text-muted-foreground">{option.description}</span>
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {isUpdating && <p className="text-xs text-muted-foreground">Updating…</p>}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}
